<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import { fetchTimesheetPdf, getTimesheet, pushEvents } from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";

const route = useRoute();
const id = computed(() => route.params.id as string);
const queryClient = useQueryClient();

const { data, isPending, isError, error } = useQuery({
  queryKey: ["timesheet", id],
  queryFn: () => getTimesheet(id.value),
});

const adjustHours = ref<string>("");
const adjustNote = ref<string>("");
watch(
  data,
  (d) => {
    if (d) {
      adjustHours.value =
        d.adjusted_hours === null ? "" : String(d.adjusted_hours);
      adjustNote.value = d.adjustment_note ?? "";
    }
  },
  { immediate: true },
);

const busy = ref(false);
const actionError = ref<string | null>(null);
const pdfBusy = ref(false);

const isApproved = computed(() => data.value?.status === "approved");

function hrs(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${value}h`;
}
function fmtDist(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

async function downloadPdf(): Promise<void> {
  pdfBusy.value = true;
  try {
    const blob = await fetchTimesheetPdf(id.value);
    window.open(URL.createObjectURL(blob), "_blank");
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    pdfBusy.value = false;
  }
}

async function saveAdjustment(): Promise<void> {
  busy.value = true;
  actionError.value = null;
  try {
    const hours = adjustHours.value.trim();
    await pushEvents([
      {
        aggregate_type: "timesheet",
        aggregate_id: id.value,
        event_type: "TimesheetAdjusted",
        payload: {
          adjusted_hours: hours === "" ? null : Number(hours),
          adjustment_note: adjustNote.value.trim(),
        },
      },
    ]);
    await queryClient.invalidateQueries({ queryKey: ["timesheet", id] });
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}

async function approve(): Promise<void> {
  busy.value = true;
  actionError.value = null;
  try {
    await pushEvents([
      {
        aggregate_type: "timesheet",
        aggregate_id: id.value,
        event_type: "TimesheetApproved",
        payload: { approved_at: new Date().toISOString() },
      },
    ]);
    await queryClient.invalidateQueries({ queryKey: ["timesheet", id] });
    await queryClient.invalidateQueries({ queryKey: ["timesheets"] });
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}

async function reopen(): Promise<void> {
  busy.value = true;
  actionError.value = null;
  try {
    await pushEvents([
      {
        aggregate_type: "timesheet",
        aggregate_id: id.value,
        event_type: "TimesheetReopened",
        payload: {},
      },
    ]);
    await queryClient.invalidateQueries({ queryKey: ["timesheet", id] });
    await queryClient.invalidateQueries({ queryKey: ["timesheets"] });
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <PageLayout>
    <RouterLink
      to="/timesheets"
      class="text-sm text-neutral-500 hover:text-neutral-900"
    >
      ← Timesheets
    </RouterLink>

    <div
      v-if="isPending"
      class="mt-4 text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="mt-4 rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data"
      class="mt-4 grid gap-6"
    >
      <header class="flex items-start justify-between gap-4">
        <div>
          <h1 class="text-2xl font-extrabold text-neutral-900">
            {{ data.worker_name }} — {{ data.rail_week }}
          </h1>
          <p class="mt-1 text-neutral-500">
            NCCA {{ data.worker_ncca_no }} ·
            {{ data.period_start }} to {{ data.period_end }}
            <span v-if="data.po_number"> · PO {{ data.po_number }}</span>
            <span v-if="data.payment_type"> · {{ data.payment_type }}</span>
          </p>
        </div>
        <button
          type="button"
          class="shrink-0 rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
          :disabled="pdfBusy"
          @click="downloadPdf"
        >
          {{ pdfBusy ? "Generating…" : "Download PDF" }}
        </button>
      </header>

      <!-- Reconciliation -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-4 font-semibold">
          Hours
        </h2>
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <p class="text-2xl font-bold">
              {{ hrs(data.reconciliation.planned_hours) }}
            </p>
            <p class="text-sm text-neutral-500">
              Planned (roster)
            </p>
          </div>
          <div>
            <p class="text-2xl font-bold">
              {{ hrs(data.reconciliation.actual_hours) }}
            </p>
            <p class="text-sm text-neutral-500">
              Actual (sign-ons)
            </p>
          </div>
          <div>
            <p class="text-2xl font-bold">
              {{ hrs(data.reconciliation.reconciled_hours) }}
            </p>
            <p class="text-sm text-neutral-500">
              Reconciled total
            </p>
          </div>
          <div>
            <p
              class="text-2xl font-bold"
              :class="(data.reconciliation.variance ?? 0) < 0 ? 'text-semantic-danger' : ''"
            >
              {{ hrs(data.reconciliation.variance) }}
            </p>
            <p class="text-sm text-neutral-500">
              Variance
            </p>
          </div>
        </div>
        <p
          v-if="!data.reconciliation.has_signons"
          class="mt-4 text-sm text-neutral-400"
        >
          No diary sign-ons found for this worker this week — showing planned
          hours.
        </p>

        <!-- Verified attendance (geofenced sign-on GPS vs the work site) -->
        <div
          v-if="data.reconciliation.geofence_checked && data.reconciliation.located_days"
          class="mt-4 flex flex-wrap items-center gap-2 text-sm"
        >
          <span class="font-medium">Verified attendance</span>
          <span class="rounded-full bg-semantic-success/10 px-2 py-0.5 text-xs text-semantic-success">
            {{ data.reconciliation.on_site_days }} on site
          </span>
          <span
            v-if="data.reconciliation.off_site_days"
            class="rounded-full bg-semantic-danger/10 px-2 py-0.5 text-xs text-semantic-danger"
          >
            {{ data.reconciliation.off_site_days }} off site
          </span>
          <span class="text-xs text-neutral-400">
            of {{ data.reconciliation.located_days }} located sign-on(s) ·
            geofence {{ data.reconciliation.geofence_radius_m }} m
          </span>
        </div>

        <!-- Site working-window (out-of-hours) flags -->
        <div
          v-if="data.reconciliation.site_hours_start"
          class="mt-2 flex flex-wrap items-center gap-2 text-sm"
        >
          <span class="font-medium">Site hours</span>
          <span class="text-xs text-neutral-400">
            {{ data.reconciliation.site_hours_start }}–{{ data.reconciliation.site_hours_end }}
          </span>
          <span
            v-if="data.reconciliation.out_of_hours_days"
            class="rounded-full bg-semantic-warning/10 px-2 py-0.5 text-xs text-semantic-warning"
          >
            {{ data.reconciliation.out_of_hours_days }} out of hours
          </span>
          <span
            v-else
            class="rounded-full bg-semantic-success/10 px-2 py-0.5 text-xs text-semantic-success"
          >
            all within hours
          </span>
        </div>

        <div
          v-if="data.reconciliation.actual_days.length"
          class="mt-6"
        >
          <h3 class="mb-2 text-sm font-semibold text-neutral-700">
            Actual shifts — from the diary
          </h3>
          <ul class="grid gap-1 text-sm">
            <li
              v-for="(d, i) in data.reconciliation.actual_days"
              :key="i"
              class="flex flex-wrap items-center gap-2 text-neutral-600"
            >
              <span>
                {{ d.date }} · {{ d.time_in ?? "—" }}–{{ d.time_out ?? "—" }} ·
                {{ hrs(d.hours) }}
              </span>
              <span
                v-if="d.on_site === true"
                class="rounded-full bg-semantic-success/10 px-2 py-0.5 text-xs text-semantic-success"
                :title="d.distance_m != null ? `${fmtDist(d.distance_m)} from site` : ''"
              >✓ on site</span>
              <span
                v-else-if="d.on_site === false"
                class="rounded-full bg-semantic-danger/10 px-2 py-0.5 text-xs text-semantic-danger"
              >⚠ off site<template v-if="d.distance_m != null"> · {{ fmtDist(d.distance_m) }}</template></span>
              <span
                v-else-if="d.latitude == null"
                class="text-xs text-neutral-300"
              >no GPS</span>
              <span
                v-if="d.out_of_hours === true"
                class="rounded-full bg-semantic-warning/10 px-2 py-0.5 text-xs text-semantic-warning"
              >⏱ out of hours</span>
            </li>
          </ul>
        </div>
      </section>

      <!-- Adjustment + approval -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-4 font-semibold">
          Adjustment & approval
        </h2>
        <div
          v-if="isApproved"
          class="mb-4 rounded-md bg-semantic-success/10 p-3 text-sm text-semantic-success"
        >
          Approved{{ data.approved_at ? ` at ${data.approved_at}` : "" }} —
          locked.
        </div>
        <div class="grid gap-4 sm:grid-cols-2">
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Adjusted hours (overrides total)</span>
            <input
              v-model="adjustHours"
              type="number"
              step="0.5"
              min="0"
              :disabled="isApproved"
              class="rounded-md border border-neutral-300 px-3 py-2 disabled:bg-neutral-50"
            >
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Adjustment note</span>
            <input
              v-model="adjustNote"
              type="text"
              :disabled="isApproved"
              placeholder="Reason for the adjustment"
              class="rounded-md border border-neutral-300 px-3 py-2 disabled:bg-neutral-50"
            >
          </label>
        </div>
        <p
          v-if="actionError"
          class="mt-3 text-sm text-semantic-danger"
        >
          {{ actionError }}
        </p>
        <div class="mt-4 flex flex-wrap gap-3">
          <button
            v-if="!isApproved"
            type="button"
            class="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
            :disabled="busy"
            @click="saveAdjustment"
          >
            Save adjustment
          </button>
          <button
            v-if="!isApproved"
            type="button"
            class="rounded-md bg-brand-primary px-4 py-2 font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
            :disabled="busy"
            @click="approve"
          >
            {{ busy ? "Working…" : "Approve & lock" }}
          </button>
          <button
            v-else
            type="button"
            class="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
            :disabled="busy"
            @click="reopen"
          >
            Reopen
          </button>
        </div>
      </section>
    </div>
  </PageLayout>
</template>
